import { ArrowRight } from 'lucide-react-native';
import { useCallback, useMemo } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { HStack, VStack } from '../../../components/primitives';
import { Select } from '../../../components/primitives/Select';
import { Text } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { ColumnMapping } from '../types';
import { CRM_FIELD_KEYS } from '../types';
import { MappingConfidenceBadge } from './MappingConfidenceBadge';

interface ColumnMappingTableProps {
  mappings: ColumnMapping[];
  onMappingChange: (index: number, crmField: string) => void;
}

export function ColumnMappingTable({
  mappings,
  onMappingChange,
}: Readonly<ColumnMappingTableProps>) {
  const { t } = useTranslation();

  const selectOptions = useMemo(
    () =>
      CRM_FIELD_KEYS.map((key) => ({
        value: key,
        label: t(`leadImport.crmFields.${key}`),
      })),
    [t],
  );

  const handleChange = useCallback(
    (index: number) => (value: string) => {
      onMappingChange(index, value);
    },
    [onMappingChange],
  );

  return (
    <VStack spacing="sm">
      <HStack justify="between" style={styles.headerRow}>
        <Text variant="caption" color={colors.text.tertiary} style={styles.headerLabel}>
          {t('leadImport.columnMapping.fileColumn')}
        </Text>
        <Text variant="caption" color={colors.text.tertiary} style={styles.headerLabel}>
          {t('leadImport.columnMapping.crmField')}
        </Text>
      </HStack>

      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
        nestedScrollEnabled
      >
        {mappings.map((mapping, index) => (
          <View
            key={mapping.header}
            style={[styles.row, Platform.OS === 'web' && { zIndex: mappings.length - index }]}
          >
            <HStack spacing="sm" align="center" style={styles.rowContent}>
              <View style={styles.headerCell}>
                <Text variant="body" color={colors.text.primary} numberOfLines={1}>
                  {mapping.header}
                </Text>
                <MappingConfidenceBadge confidence={mapping.confidence} />
              </View>

              <ArrowRight size={14} color={colors.text.tertiary} />

              <View style={styles.selectCell}>
                <Select
                  variant="dropdown"
                  options={selectOptions}
                  value={mapping.crmField}
                  onChange={handleChange(index)}
                  testID={`mapping_select_${index}`}
                  style={styles.selectOverride}
                />
              </View>
            </HStack>
          </View>
        ))}
        <View style={styles.bottomSpacer} />
      </ScrollView>
    </VStack>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    paddingHorizontal: tokens.spacing.md,
    paddingBottom: tokens.spacing.xs,
  },
  headerLabel: {
    flex: 1,
  },
  scrollContainer: {
    maxHeight: 400,
  },
  scrollContent: {
    gap: tokens.spacing.xs,
  },
  row: {
    backgroundColor: colors.background.secondary,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  rowContent: {
    flex: 1,
  },
  headerCell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  selectCell: {
    flex: 1,
  },
  selectOverride: {
    ...Platform.select({
      web: { position: 'relative' as any, zIndex: 1 },
      default: {},
    }),
  },
  bottomSpacer: {
    height: 250,
  },
});
